const MINUTES_IN_DAY = 24 * 60;
const DEFAULT_SCHEDULE_START = 9 * 60;
const DEFAULT_SCHEDULE_END = 18 * 60;

function formatMinutes(total) {
  const mins = Math.max(0, Math.round(total));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, "0")}min`;
}

function formatHourLabel(minutes) {
  const h = Math.floor(minutes / 60);
  return `${String(h).padStart(2, "0")}:00`;
}

function shiftDate(dateStr, deltaDays) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

function formatDateHeading(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return d.toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "long",
  });
}

function localDateString(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isTodayDate(dateStr) {
  return dateStr === localDateString();
}

function minutesFromIso(iso) {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
}

function barStyleFromSession(session) {
  const startMin = minutesFromIso(session.start);
  let endMin = minutesFromIso(session.end);
  if (endMin < startMin) endMin = MINUTES_IN_DAY;
  const left = (startMin / MINUTES_IN_DAY) * 100;
  const width = Math.max(((endMin - startMin) / MINUTES_IN_DAY) * 100, 0.4);
  return { left: `${left}%`, width: `${width}%` };
}

function scheduleOverlayStyle(
  startMin = DEFAULT_SCHEDULE_START,
  endMin = DEFAULT_SCHEDULE_END,
) {
  return {
    left: `${(startMin / MINUTES_IN_DAY) * 100}%`,
    width: `${((endMin - startMin) / MINUTES_IN_DAY) * 100}%`,
  };
}

function nowLineStyle(nowIso, dateStr) {
  if (!isTodayDate(dateStr)) return null;
  const left = (minutesFromIso(nowIso) / MINUTES_IN_DAY) * 100;
  return `${left}%`;
}

const HOUR_TICKS = Array.from({ length: 13 }, (_, i) => i * 2 * 60);

function formatElapsedSeconds(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}h ${String(m).padStart(2, "0")}m ${String(sec).padStart(2, "0")}s`;
  }
  if (m > 0) {
    return `${m}m ${String(sec).padStart(2, "0")}s`;
  }
  return `${sec}s`;
}

function computeLiveSessionSeconds(entries, status, now = new Date()) {
  if (!status?.isClockedIn) return 0;

  const active = entries
    .filter((e) => !e.isSuperseded)
    .sort((a, b) => new Date(a.recordedAt) - new Date(b.recordedAt));

  let lastClockInIdx = -1;
  for (let i = active.length - 1; i >= 0; i -= 1) {
    if (active[i].eventType === "clock_in") {
      lastClockInIdx = i;
      break;
    }
  }
  if (lastClockInIdx < 0) return 0;

  const sessionEntries = active.slice(lastClockInIdx);
  let workedMs = 0;
  let workStart = null;

  for (const entry of sessionEntries) {
    const at = new Date(entry.recordedAt);
    if (entry.eventType === "clock_in") {
      workStart = at;
    } else if (entry.eventType === "pause_start" && workStart) {
      workedMs += at - workStart;
      workStart = null;
    } else if (entry.eventType === "pause_end") {
      workStart = at;
    } else if (entry.eventType === "clock_out" && workStart) {
      workedMs += at - workStart;
      workStart = null;
    }
  }

  if (workStart && !status.isOnPause) {
    workedMs += now - new Date(workStart);
  }

  return Math.max(0, Math.floor(workedMs / 1000));
}

function computeTodayWorkedSeconds(entries, now = new Date(), status = null) {
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(now);
  dayEnd.setHours(23, 59, 59, 999);

  const sorted = entries
    .filter((e) => {
      if (e.isSuperseded) return false;
      const t = new Date(e.recordedAt);
      return t >= dayStart && t <= dayEnd;
    })
    .sort((a, b) => new Date(a.recordedAt) - new Date(b.recordedAt));

  let workedMs = 0;
  let workStart = null;

  for (const entry of sorted) {
    const at = new Date(entry.recordedAt);
    if (entry.eventType === "clock_in") {
      workStart = at;
    } else if (entry.eventType === "pause_start" && workStart) {
      workedMs += at - workStart;
      workStart = null;
    } else if (entry.eventType === "pause_end") {
      workStart = at;
    } else if (entry.eventType === "clock_out" && workStart) {
      workedMs += at - workStart;
      workStart = null;
    }
  }

  if (workStart && !status?.isOnPause) {
    workedMs += now - new Date(workStart);
  }

  return Math.max(0, Math.floor(workedMs / 1000));
}

export {
  MINUTES_IN_DAY,
  DEFAULT_SCHEDULE_START,
  DEFAULT_SCHEDULE_END,
  formatMinutes,
  formatElapsedSeconds,
  computeLiveSessionSeconds,
  computeTodayWorkedSeconds,
  formatHourLabel,
  shiftDate,
  formatDateHeading,
  isTodayDate,
  localDateString,
  barStyleFromSession,
  scheduleOverlayStyle,
  nowLineStyle,
  HOUR_TICKS,
};
